import { concat } from '@ember/helper';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import { eq } from 'ember-truth-helpers';

// This component does not use the fui-checkbox modifier from wise-fui-modules
// so we have to import the module here to make sure the JQuery `checkbox` plugin
// in properly installed
import 'wise-fui-modules/vendor/checkbox.min.js';

interface CheckboxParams {
  onChange?: (value: string | string[] | boolean) => void;
}

export interface Signature {
  Element: HTMLDivElement;
  Args: {
    disabled?: boolean;
    checked?: boolean;
    isReadOnly?: boolean;
    payloadKey?: string;
    dataTestInput?: string;
    labelName?: string;
    isMandatory?: boolean;
    // Narrower than CheckboxParams['onChange']: this component only ever
    // invokes it with the checkbox's "is checked" boolean state.
    onChange?: (value: boolean) => void;
  };
  Blocks: { default: [] };
}

interface HTMLCheckboxElement extends JQuery {
  /**
   * Type is wide because the method "checkbox" as different signature
   * Can be used:
   *  - checkbox(command: string)
   *  - checkbox(options: CheckboxParams)
   */
  checkbox(args: CheckboxParams): void;
  checkbox(command: 'is checked'): boolean;
  checkbox(command: string): string;
}

export default class Checkbox extends Component<Signature> {
  get elementId() {
    return guidFor(this);
  }

  _updateCheckbox = (element: HTMLElement) => {
    ($(element) as HTMLCheckboxElement).checkbox({
      onChange: () => {
        this.args.onChange?.(
          ($(element) as HTMLCheckboxElement).checkbox('is checked')
        );
      }
    });

    if (this.args.disabled || this.args.isReadOnly) {
      ($(element) as HTMLCheckboxElement).checkbox('set disabled');
    } else {
      ($(element) as HTMLCheckboxElement).checkbox('set enabled');
    }

    if (this.args.checked) {
      ($(element) as HTMLCheckboxElement).checkbox('set checked');
    } else {
      ($(element) as HTMLCheckboxElement).checkbox('set unchecked');
    }
  };

  updateCheckbox = modifier(this._updateCheckbox);

  <template>
    <div
      class='ui checkbox'
      data-test-input={{if @dataTestInput @dataTestInput}}
      data-test-checkbox-for={{if @payloadKey @payloadKey}}
      ...attributes
      {{this.updateCheckbox}}
    >
      <input
        id={{this.elementId}}
        type='checkbox'
        readonly={{eq @isReadOnly true}}
      />
      <label for={{this.elementId}}>
        {{#if (has-block)}}
          {{yield}}
        {{else}}
          {{concat @labelName (if @isMandatory ' *')}}
        {{/if}}
      </label>
    </div>
  </template>
}
